import React, { useEffect, useRef, useState } from "react";

const formatTime = (time: number): string => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const useAudioSetup = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  const initializeAudio = () => {
    if (!audioRef.current || audioContextRef.current) return;

    try {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
      gainNodeRef.current = audioContextRef.current.createGain();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;

      sourceRef.current
        .connect(gainNodeRef.current)
        .connect(analyserRef.current)
        .connect(audioContextRef.current.destination);
    } catch (error) {
      console.error("Audio setup error:", error);
    }
  };

  useEffect(() => {
    return () => {
      if (sourceRef.current) sourceRef.current.disconnect();
      if (gainNodeRef.current) gainNodeRef.current.disconnect();
      if (analyserRef.current) analyserRef.current.disconnect();
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, []);

  return { audioRef, audioContextRef, analyserRef, gainNodeRef, initializeAudio };
};

const AudioControls: React.FC<{
  isPlaying: boolean;
  onPlayPause: () => void;
  onVolumeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  volume: number;
  currentTime: number;
  duration: number;
}> = ({ isPlaying, onPlayPause, onVolumeChange, volume, currentTime, duration }) => (
  <div className="space-y-4">
    <div className="flex items-center space-x-4">
      <button onClick={onPlayPause} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
        {isPlaying ? "Pause" : "Play"}
      </button>
      <div className="flex items-center space-x-2">
        <span className="text-sm">{formatTime(currentTime)}</span>
        <div className="w-48 h-1 bg-gray-300 rounded">
          <div className="h-full bg-blue-500 rounded" style={{ width: `${(currentTime / duration) * 100}%` }}></div>
        </div>
        <span className="text-sm">{formatTime(duration)}</span>
      </div>
    </div>
    <div className="flex items-center space-x-2">
      <span className="text-sm">Volume:</span>
      <input type="range" min="0" max="1" step="0.1" value={volume} onChange={onVolumeChange} className="w-32" />
    </div>
  </div>
);

const SimpleTest: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVibrating, setIsVibrating] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [logs, setLogs] = useState<string[]>([]);
  const [energy, setEnergy] = useState(0);
  const { audioRef, audioContextRef, analyserRef, gainNodeRef, initializeAudio } = useAudioSetup();
  const [error, setError] = useState<string>("");
  const [isLooping, setIsLooping] = useState(false);

  const toggleLoop = () => {
    setIsLooping(!isLooping);
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = isLooping;
    }
  }, [isLooping]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.load();
      audioRef.current.onerror = (e: Event) => {
        const error = (e.target as HTMLAudioElement).error;
        setError(error?.message || "Error loading audio");
        console.error("Audio error:", error);
      };
    }
  }, []);

  useEffect(() => {
    if (!analyserRef.current) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const detectBeat = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
      setEnergy(average);

      if (average > 100 && isVibrating) {
        navigator.vibrate(50);
      }

      setLogs((prevLogs) => [...prevLogs, `Time: ${formatTime(currentTime)}, Energy: ${average.toFixed(2)}`]);

      if (isPlaying) {
        requestAnimationFrame(detectBeat);
      }
    };

    if (isPlaying) {
      detectBeat();
    }
  }, [isPlaying, isVibrating, analyserRef]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
    };
  }, [audioRef]);

  const handlePlayPause = async () => {
    if (!audioRef.current) return;

    try {
      // Initialize audio context on first play
      if (!audioContextRef.current) {
        initializeAudio();
      }

      if (audioContextRef.current?.state === "suspended") {
        await audioContextRef.current.resume();
      }

      if (isPlaying) {
        audioRef.current.pause();
      } else {
        await audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    } catch (error) {
      console.error("Error playing audio:", error);
      setError("Failed to play audio");
    }
  };

  const toggleVibration = () => {
    setIsVibrating(!isVibrating);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);

    // Use gain node for volume control instead of audio element
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = newVolume;
    }
  };

  return (
    <div className="space-y-4">
      <audio
        ref={audioRef}
        src="/Gold soul snip.mp3"
        preload="auto"
        crossOrigin="anonymous"
        onPlay={() => console.log("Audio play event triggered")}
        onCanPlay={() => console.log("Audio can play")}
        onLoadedData={() => console.log("Audio data loaded")}
        onError={(e) => console.error("Audio error:", e)}
      />
      {error && <div className="text-red-500">Error: {error}</div>}
      <AudioControls
        isPlaying={isPlaying}
        onPlayPause={handlePlayPause}
        onVolumeChange={handleVolumeChange}
        volume={volume}
        currentTime={currentTime}
        duration={duration}
      />
      <button
        onClick={toggleVibration}
        className={`${
          isVibrating ? "bg-green-500 hover:bg-green-700" : "bg-red-500 hover:bg-red-700"
        } text-white font-bold py-2 px-4 rounded`}
      >
        {isVibrating ? "Disable Vibration" : "Enable Vibration"}
      </button>
      <button
        onClick={toggleLoop}
        className={`${
          isLooping ? "bg-yellow-500 hover:bg-yellow-700" : "bg-gray-500 hover:bg-gray-700"
        } text-white font-bold py-2 px-4 rounded`}
      >
        {isLooping ? "Disable Loop" : "Enable Loop"}
      </button>
      <div className="space-y-2">
        <p>Current Time: {formatTime(currentTime)}</p>
        <p>Duration: {formatTime(duration)}</p>
        <p>Energy: {energy.toFixed(2)}</p>
      </div>
      <div className="mt-4">
        <h3 className="text-lg font-semibold mb-2">Logs:</h3>
        <div className="bg-gray-800 p-4 rounded-lg h-40 overflow-y-auto">
          {logs.map((log, index) => (
            <p key={index} className="text-sm text-gray-300">
              {log}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

interface VibrationPoint {
  time: number;
  energy: number;
}

const FrequencyTest: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVibrating, setIsVibrating] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [logs, setLogs] = useState<string[]>([]);
  const [energy, setEnergy] = useState(0);
  const [offset, setOffset] = useState(0); // Offset in milliseconds
  const { audioRef, audioContextRef, analyserRef, gainNodeRef, initializeAudio } = useAudioSetup();
  const [error, setError] = useState<string>("");
  const [isLooping, setIsLooping] = useState(false);
  const [bassFrequency, setBassFrequency] = useState(250);
  const [midFrequency, setMidFrequency] = useState(4000);
  const [highFrequency, setHighFrequency] = useState(12000);
  const [predictedVibrations, setPredictedVibrations] = useState(0);
  const [actualVibrations, setActualVibrations] = useState(0);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [vibrationPoints, setVibrationPoints] = useState<VibrationPoint[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const waveformRef = useRef<HTMLCanvasElement>(null);
  const [fullWaveformData, setFullWaveformData] = useState<Float32Array | null>(null);
  const [analyzedPoints, setAnalyzedPoints] = useState<VibrationPoint[]>([]);
  const [waveformScale, setWaveformScale] = useState(1.0);
  const [offsetPixels, setOffsetPixels] = useState(0);

  const toggleLoop = () => {
    setIsLooping(!isLooping);
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = isLooping;
    }
  }, [isLooping]);

  const handleBassFrequencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newBass = parseInt(e.target.value);
    if (newBass < midFrequency) {
      setBassFrequency(newBass);
    }
  };

  const handleMidFrequencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMid = parseInt(e.target.value);
    if (newMid > bassFrequency && newMid < highFrequency) {
      setMidFrequency(newMid);
    }
  };

  const handleHighFrequencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHigh = parseInt(e.target.value);
    if (newHigh > midFrequency) {
      setHighFrequency(newHigh);
    }
  };

  const handleOffsetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(parseInt(e.target.value));
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.load();
      audioRef.current.onerror = (e: Event) => {
        const error = (e.target as HTMLAudioElement).error;
        setError(error?.message || "Error loading audio");
        console.error("Audio error:", error);
      };
    }
  }, []);

  useEffect(() => {
    if (!analyserRef.current) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    let vibrationCount = 0;

    const detectBeat = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArray);

      // Calculate average for each frequency band
      const bassIndex = Math.floor((bassFrequency / (audioContextRef.current!.sampleRate / 2)) * bufferLength);
      const midIndex = Math.floor((midFrequency / (audioContextRef.current!.sampleRate / 2)) * bufferLength);
      const highIndex = Math.floor((highFrequency / (audioContextRef.current!.sampleRate / 2)) * bufferLength);

      const bassData = dataArray.slice(0, bassIndex);
      const midData = dataArray.slice(bassIndex, midIndex);
      const highData = dataArray.slice(midIndex, highIndex);

      const bassAvg = bassData.reduce((sum, value) => sum + value, 0) / bassData.length;
      const midAvg = midData.reduce((sum, value) => sum + value, 0) / midData.length;
      const highAvg = highData.reduce((sum, value) => sum + value, 0) / highData.length;

      // Determine the number of vibrations based on frequency energies
      let vibrationPattern: number[] = [];
      const totalVibrations = Math.floor((bassAvg + midAvg + highAvg) / 100); // Adjust scaling as needed

      for (let i = 0; i < totalVibrations; i++) {
        vibrationPattern.push(50); // Vibration duration
        vibrationPattern.push(30); // Pause duration
      }

      if (totalVibrations > 0 && isVibrating) {
        navigator.vibrate(vibrationPattern);
        setActualVibrations((prev) => prev + totalVibrations);
      }

      setLogs((prevLogs) => [
        ...prevLogs,
        `Time: ${formatTime(currentTime)}, Bass: ${bassAvg.toFixed(2)}, Mid: ${midAvg.toFixed(
          2
        )}, High: ${highAvg.toFixed(2)}, Vibrations: ${totalVibrations}`,
      ]);

      if (isPlaying) {
        requestAnimationFrame(detectBeat);
      }
    };

    if (isPlaying) {
      detectBeat();
    }
  }, [isPlaying, isVibrating, analyserRef, bassFrequency, midFrequency, highFrequency, currentTime]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
    };
  }, [audioRef]);

  useEffect(() => {
    if (duration > 0) {
      // Rough estimation: predict one vibration every 0.5 seconds
      const predicted = Math.ceil(duration / 0.5);
      setPredictedVibrations(predicted);
    }
  }, [duration]);

  useEffect(() => {
    if (!waveformRef.current || !analyserRef.current) return;

    const canvas = waveformRef.current;
    const ctx = canvas.getContext("2d")!;
    const width = canvas.width;
    const height = canvas.height;
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const drawWaveform = () => {
      if (!analyserRef.current) return;
      analyserRef.current.getByteTimeDomainData(dataArray);

      ctx.fillStyle = "rgb(20, 20, 20)";
      ctx.fillRect(0, 0, width, height);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "rgb(100, 100, 255)";
      ctx.beginPath();

      const sliceWidth = width / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * height) / 2;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      ctx.lineTo(width, height / 2);
      ctx.stroke();

      if (isPlaying) {
        requestAnimationFrame(drawWaveform);
      }
    };

    if (isPlaying) {
      drawWaveform();
    }
  }, [isPlaying, analyserRef.current]);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    const width = canvas.width;
    const height = canvas.height;

    ctx.fillStyle = "rgb(20, 20, 20)";
    ctx.fillRect(0, 0, width, height);

    // Draw time markers
    ctx.fillStyle = "rgb(40, 40, 40)";
    for (let i = 0; i < duration; i++) {
      const x = (i / duration) * width;
      ctx.fillRect(x, 0, 1, height);
    }

    // Draw vibration points with offset
    ctx.fillStyle = "rgb(0, 255, 0)";
    vibrationPoints.forEach((point) => {
      const x = ((point.time + offset / 1000) / duration) * width;
      if (x >= 0 && x <= width) {
        ctx.fillRect(x - 1, 0, 2, height);
        const energyHeight = (point.energy / 255) * height;
        ctx.fillRect(x - 1, height - energyHeight, 2, energyHeight);
      }
    });

    // Draw playhead
    if (currentTime > 0) {
      ctx.fillStyle = "rgb(255, 0, 0)";
      const playheadX = (currentTime / duration) * width;
      ctx.fillRect(playheadX - 1, 0, 2, height);
    }
  }, [vibrationPoints, offset, currentTime, duration]);

  useEffect(() => {
    if (!audioRef.current || !audioContextRef.current) return;

    const analyzeFullAudio = async () => {
      try {
        const response = await fetch(audioRef.current!.src);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContextRef.current!.decodeAudioData(arrayBuffer);

        // Get the full waveform data
        const channelData = audioBuffer.getChannelData(0);
        setFullWaveformData(channelData);

        // Analyze for potential vibration points
        const sampleRate = audioBuffer.sampleRate;
        const pointsToAnalyze = Math.floor(audioBuffer.duration * 10); // 10 samples per second
        const samplesPerPoint = Math.floor(channelData.length / pointsToAnalyze);

        const points: VibrationPoint[] = [];

        for (let i = 0; i < pointsToAnalyze; i++) {
          const start = i * samplesPerPoint;
          const end = start + samplesPerPoint;
          const segment = channelData.slice(start, end);

          // Calculate RMS energy for this segment
          const energy = Math.sqrt(segment.reduce((sum, x) => sum + x * x, 0) / segment.length);

          // If energy exceeds threshold, mark as potential vibration point
          if (energy > 0.1) {
            // Adjust threshold as needed
            points.push({
              time: i / 10, // Convert to seconds
              energy: energy * 100, // Scale for visualization
            });
          }
        }

        setAnalyzedPoints(points);
        setPredictedVibrations(points.length);
      } catch (error) {
        console.error("Error analyzing audio:", error);
      }
    };

    analyzeFullAudio();
  }, [audioRef.current, audioContextRef.current]);

  useEffect(() => {
    if (!waveformRef.current || !fullWaveformData) return;

    const canvas = waveformRef.current;
    const ctx = canvas.getContext("2d")!;
    const width = canvas.width;
    const height = canvas.height;

    ctx.fillStyle = "rgb(20, 20, 20)";
    ctx.fillRect(0, 0, width, height);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgb(100, 100, 255)";
    ctx.beginPath();

    const step = Math.ceil(fullWaveformData.length / width);
    const amp = height / 2;

    for (let i = 0; i < width; i++) {
      let min = 1.0;
      let max = -1.0;
      for (let j = 0; j < step; j++) {
        const datum = fullWaveformData[i * step + j];
        if (datum < min) min = datum;
        if (datum > max) max = datum;
      }
      ctx.moveTo(i, (1 + min) * amp);
      ctx.lineTo(i, (1 + max) * amp);
    }

    ctx.stroke();
  }, [fullWaveformData]);

  useEffect(() => {
    if (!canvasRef.current || !analyzedPoints.length) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    const width = canvas.width;
    const height = canvas.height;

    ctx.fillStyle = "rgb(20, 20, 20)";
    ctx.fillRect(0, 0, width, height);

    // Draw time markers
    ctx.fillStyle = "rgb(40, 40, 40)";
    for (let i = 0; i < duration; i++) {
      const x = (i / duration) * width;
      ctx.fillRect(x, 0, 1, height);
    }

    // Draw analyzed points with offset
    ctx.fillStyle = "rgb(0, 255, 0)";
    analyzedPoints.forEach((point) => {
      const x = ((point.time + offset / 1000) / duration) * width;
      if (x >= 0 && x <= width) {
        const energyHeight = (point.energy / 100) * height;
        ctx.fillRect(x - 1, height - energyHeight, 2, energyHeight);
      }
    });

    // Draw playhead
    if (currentTime > 0) {
      ctx.fillStyle = "rgb(255, 0, 0)";
      const playheadX = (currentTime / duration) * width;
      ctx.fillRect(playheadX - 1, 0, 2, height);
    }
  }, [analyzedPoints, offset, currentTime, duration]);

  useEffect(() => {
    if (!waveformRef.current || !fullWaveformData) return;

    const canvas = waveformRef.current;
    const ctx = canvas.getContext("2d")!;
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.fillStyle = "rgb(20, 20, 20)";
    ctx.fillRect(0, 0, width, height);

    // Draw center line
    ctx.strokeStyle = "rgb(40, 40, 40)";
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.stroke();

    // Draw waveform
    ctx.strokeStyle = "rgb(100, 100, 255)";
    ctx.beginPath();

    const samplesPerPixel = Math.floor(fullWaveformData.length / width);
    const centerY = height / 2;

    for (let x = 0; x < width; x++) {
      const sampleIndex = x * samplesPerPixel;
      const sample = fullWaveformData.slice(sampleIndex, sampleIndex + samplesPerPixel);

      // Find min and max values in this sample window
      const min = Math.min(...sample) * waveformScale;
      const max = Math.max(...sample) * waveformScale;

      // Draw vertical line for this sample
      ctx.moveTo(x + offsetPixels, centerY + min * centerY);
      ctx.lineTo(x + offsetPixels, centerY + max * centerY);
    }
    ctx.stroke();

    // Draw vibration window
    ctx.strokeStyle = "rgba(255, 0, 0, 0.5)";
    ctx.strokeRect(width / 2 - 50, 0, 100, height);
  }, [fullWaveformData, offset, waveformScale, offsetPixels]);

  // Update offset in pixels when offset (ms) changes
  useEffect(() => {
    if (!fullWaveformData || !duration) return;
    const pixelsPerSecond = waveformRef.current!.width / duration;
    setOffsetPixels((offset * pixelsPerSecond) / 1000);
  }, [offset, duration, fullWaveformData]);

  // Add zoom controls
  const handleZoomIn = () => setWaveformScale((prev) => prev * 1.2);
  const handleZoomOut = () => setWaveformScale((prev) => prev / 1.2);

  const handlePlayPause = async () => {
    if (!audioRef.current) return;

    try {
      if (!audioContextRef.current) {
        initializeAudio();
      }

      if (audioContextRef.current?.state === "suspended") {
        await audioContextRef.current.resume();
      }

      if (isPlaying) {
        audioRef.current.pause();
      } else {
        await audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    } catch (error) {
      console.error("Error playing audio:", error);
      setError("Failed to play audio");
    }
  };

  const toggleVibration = () => {
    setIsVibrating(!isVibrating);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);

    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = newVolume;
    }
  };

  return (
    <div className="space-y-4">
      <audio
        ref={audioRef}
        src="/Gold soul snip.mp3"
        preload="auto"
        crossOrigin="anonymous"
        onPlay={() => console.log("Audio play event triggered")}
        onCanPlay={() => console.log("Audio can play")}
        onLoadedData={() => console.log("Audio data loaded")}
        onError={(e) => console.error("Audio error:", e)}
      />
      {error && <div className="text-red-500">Error: {error}</div>}
      <AudioControls
        isPlaying={isPlaying}
        onPlayPause={handlePlayPause}
        onVolumeChange={handleVolumeChange}
        volume={volume}
        currentTime={currentTime}
        duration={duration}
      />
      <button
        onClick={toggleVibration}
        className={`${
          isVibrating ? "bg-green-500 hover:bg-green-700" : "bg-red-500 hover:bg-red-700"
        } text-white font-bold py-2 px-4 rounded`}
      >
        {isVibrating ? "Disable Vibration" : "Enable Vibration"}
      </button>
      <button
        onClick={toggleLoop}
        className={`${
          isLooping ? "bg-yellow-500 hover:bg-yellow-700" : "bg-gray-500 hover:bg-gray-700"
        } text-white font-bold py-2 px-4 rounded`}
      >
        {isLooping ? "Disable Loop" : "Enable Loop"}
      </button>
      <div className="space-y-2">
        <p>Current Time: {formatTime(currentTime)}</p>
        <p>Duration: {formatTime(duration)}</p>
        <p>Energy: {energy.toFixed(2)}</p>
      </div>
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">Frequency Bands:</h3>
        <div className="flex items-center space-x-2">
          <span>Bass: {bassFrequency} Hz</span>
          <input
            type="range"
            min="20"
            max="250"
            step="10"
            value={bassFrequency}
            onChange={handleBassFrequencyChange}
            className="w-full"
          />
        </div>
        <div className="flex items-center space-x-2">
          <span>Mid: {midFrequency} Hz</span>
          <input
            type="range"
            min="250"
            max="4000"
            step="100"
            value={midFrequency}
            onChange={handleMidFrequencyChange}
            className="w-full"
          />
        </div>
        <div className="flex items-center space-x-2">
          <span>High: {highFrequency} Hz</span>
          <input
            type="range"
            min="4000"
            max="22000"
            step="1000"
            value={highFrequency}
            onChange={handleHighFrequencyChange}
            className="w-full"
          />
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-lg font-semibold">Beat Offset:</h3>
        <div className="flex items-center space-x-2">
          <span>Offset: {offset} ms</span>
          <input
            type="range"
            min="-500"
            max="500"
            step="10"
            value={offset}
            onChange={handleOffsetChange}
            className="w-full"
          />
          <span>{offset} ms</span>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-lg font-semibold">Vibration Timeline:</h3>
        <canvas ref={canvasRef} width={800} height={100} className="w-full bg-gray-900 rounded" />
        <div className="flex justify-between text-sm">
          <span>0:00</span>
          <span>{formatTime(duration)}</span>
        </div>
        <p className="text-sm">{analyzedPoints.length} potential vibration points detected</p>
      </div>

      <div className="space-y-2">
        <h3 className="text-lg font-semibold">Waveform:</h3>
        <div className="relative">
          <canvas ref={waveformRef} width={800} height={200} className="w-full bg-gray-900 rounded" />
          <div className="absolute top-2 right-2 space-x-2">
            <button onClick={handleZoomIn} className="bg-gray-700 text-white px-2 py-1 rounded">
              Zoom +
            </button>
            <button onClick={handleZoomOut} className="bg-gray-700 text-white px-2 py-1 rounded">
              Zoom -
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Test: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"simple" | "advanced" | "frequency">("simple");

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Haptics Music Test</h1>
      <div className="mb-4">
        <button
          onClick={() => setActiveTab("simple")}
          className={`mr-2 px-4 py-2 rounded ${
            activeTab === "simple" ? "bg-blue-500 text-white" : "bg-gray-300 text-gray-800"
          }`}
        >
          Simple
        </button>
        <button
          onClick={() => setActiveTab("frequency")}
          className={`ml-2 px-4 py-2 rounded ${
            activeTab === "frequency" ? "bg-blue-500 text-white" : "bg-gray-300 text-gray-800"
          }`}
        >
          Frequency
        </button>
      </div>
      {activeTab === "simple" ? <SimpleTest /> : activeTab === "advanced" ? <></> : <FrequencyTest />}
    </div>
  );
};
